import React, { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { documentService } from "../../../services/api/documentService";
import { firebaseService } from "../../../services/api/firebaseService";
import Loading from "../components/Loading";
import Modal from "../components/Modal";
import { Editor } from "primereact/editor";
import CustomInput from "../components/CustomInput";

const DocumentManagement = () => {
  const { token } = useContext(AuthContext);
  const [pageLoading, setPageLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [documents, setDocuments] = useState({});
  const [editedContent, setEditedContent] = useState("");
  const [editedDocument, setEditedDocument] = useState({});

  // Modal state
  const [showCancelModal, setShowCancelModal] = useState(false);

  const fetchData = useCallback(
    async (search = "") => {
      try {
        setPageLoading(true);
        const response = await documentService.get(search, token);
        const doc = response.data?.documents?.[0] || {};

        setDocuments(doc);
        setEditedDocument(doc);

        const html = doc.pdfUrl || "";
        setFileContent(html);
        setEditedContent(html);
      } catch (error) {
        toast.dismiss();
        toast.error(
          error?.response?.data?.message ||
          "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
        );
        console.error("Error:", error);
      } finally {
        setPageLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Extract Firebase image URLs from HTML content */
  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");

    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && src.startsWith("https://storage.googleapis.com/")) {
        firebaseUrls.push(src);
      }
    });

    return firebaseUrls;
  };

  /** Process images in HTML content and upload to Firebase */
  const processImagesInContent = async (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") {
      return htmlContent || "";
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");

    if (images.length === 0) {
      return htmlContent;
    }

    // Process each image
    for (const img of images) {
      const src = img.getAttribute("src");

      // Skip if already a Firebase URL
      if (!src || src.startsWith("https://storage.googleapis.com/")) {
        continue;
      }

      // Check if it's a base64 image
      if (src.startsWith("data:image/")) {
        try {
          // Convert base64 to File
          const fetchRes = await fetch(src);
          const blob = await fetchRes.blob();
          const fileName = `editor-image-${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });

          // Upload to Firebase
          const uploadRes = await firebaseService.upload_file(token, file);
          const uploadedUrl = uploadRes?.url;

          if (!uploadedUrl) {
            console.error("Upload response:", uploadRes);
            throw new Error("Upload ảnh thất bại - URL không hợp lệ");
          }

          // Replace src with Firebase URL
          img.setAttribute("src", uploadedUrl);
        } catch (error) {
          console.error("Error uploading image:", error);
          toast.error(`Không thể upload ảnh trong nội dung: ${error.message}`);
          throw error;
        }
      }
    }

    // Return processed HTML
    return doc.body.innerHTML;
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);

      // 1. Lưu content cũ và danh sách ảnh cũ
      let oldImageUrls = [];
      let oldTxtUrl = documents.pdfUrl || "";

      if (oldTxtUrl) {
        oldImageUrls = extractFirebaseImageUrls(oldTxtUrl);
      }

      // 2. Process images in new content and upload to Firebase
      console.log("Processing images in content...");
      const processedContent = await processImagesInContent(editedContent);
      console.log("Content processed successfully");

      // 3. Tìm ảnh mới sau khi process
      const newImageUrls = extractFirebaseImageUrls(processedContent);
      console.log("New images after processing:", newImageUrls);

      // 4. Tạo FormData và gửi lên server
      const formData = new FormData();
      formData.append("name", editedDocument.name);
      formData.append("pdfUrl", processedContent);

      console.log("Submitting to API...");
      await documentService.update(documents._id, formData, token);

      // 5. XÓA CÁC FILE CŨ TRÊN FIREBASE (sau khi API thành công)
      // 5a. Xóa txtUrl cũ (file HTML cũ)
      if (
        oldTxtUrl &&
        oldTxtUrl.startsWith("https://storage.googleapis.com/")
      ) {
        try {
          console.log("Deleting old txtUrl:", oldTxtUrl);
          await firebaseService.delete_file(token, oldTxtUrl);
          console.log("✓ Deleted old txtUrl successfully");
        } catch (err) {
          console.warn("✗ Failed to delete old txtUrl:", oldTxtUrl, err);
        }
      }

      // 5b. Xóa ảnh cũ không còn sử dụng
      if (oldImageUrls.length > 0) {
        const imagesToDelete = oldImageUrls.filter(
          (url) => !newImageUrls.includes(url),
        );

        console.log("Images to delete:", imagesToDelete);

        if (imagesToDelete.length > 0) {
          console.log("Cleaning up old images:", imagesToDelete);

          for (const imageUrl of imagesToDelete) {
            try {
              console.log("Deleting old image:", imageUrl);
              await firebaseService.delete_file(token, imageUrl);
              console.log("✓ Deleted successfully:", imageUrl);
            } catch (err) {
              console.warn("✗ Failed to delete old image:", imageUrl, err);
            }
          }
        } else {
          console.log("No old images to delete");
        }
      }

      // 6. Cập nhật state sau khi lưu thành công
      setDocuments(editedDocument);
      setFileContent(processedContent);
      setIsEditMode(false);

      toast.success("Bài viết được cập nhật thành công!");
    } catch (error) {
      console.error("Save error:", error);
      toast.dismiss();
      toast.error(
        error.response
          ? `${error.response?.data?.message}`
          : error.message ||
          "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    // Reset về dữ liệu gốc
    setEditedDocument(documents);
    setEditedContent(fileContent);
    setIsEditMode(false);
    setShowCancelModal(false);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleContentChange = (e) => {
    setEditedContent(e.htmlValue);
  };

  // Loading toàn trang khi fetch initial data
  if (pageLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[16px] border border-[#D2D5DA] p-4 h-full overflow-auto space-y-6">
      {/* Loading overlay khi đang save */}
      {saveLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <Loading />
            <p className="mt-4 text-gray-700">Đang lưu bài viết...</p>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Xác nhận hủy"
        maxWidth="420px"
        cancelButtonText="Quay lại"
        showCancelButton
        primaryButtonText="Hủy thay đổi"
        primaryButtonVariant="danger"
        primaryButtonOnClick={confirmCancel}
      >
        <div className="px-6 py-4">
          <p className="text-gray-700">
            Bạn có chắc chắn muốn hủy thao tác không?
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Các thay đổi sẽ không được lưu.
          </p>
        </div>
      </Modal>

      {/* Header Actions */}
      <div className="w-full flex justify-end gap-3">
        {isEditMode ? (
          <>
            <button
              onClick={handleCancel}
              disabled={saveLoading}
              className="flex items-center justify-center border bg-gray-300 text-gray-700 hover:bg-gray-400 rounded-lg py-2 px-4 disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              onClick={handleSave}
              disabled={saveLoading}
              className="flex items-center justify-center border bg-[#0457C2] border-[#0457C2] text-white hover:bg-blue-600 rounded-lg py-2 px-4 disabled:opacity-50"
            >
              {saveLoading ? "Đang lưu..." : "Lưu"}
            </button>
          </>
        ) : (
          <button
            onClick={handleEdit}
            className="flex items-center justify-center border bg-[#0457C2] border-[#0457C2] text-white hover:bg-blue-600 rounded-lg py-2 px-[10px] gap-[5px]"
          >
            <img src="/icons/edit.svg" alt="Edit icon" />
            Chỉnh sửa
          </button>
        )}
      </div>

      {/* Content Area */}
      {isEditMode ? (
        <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
          <div className="flex flex-col gap-1">
            <strong className="text-gray-700">Đường dẫn tới trang web:</strong>
            <CustomInput
              placeholder="Nhập đường dẫn tới trang web"
              value={editedDocument.name}
              required={true}
              onChange={(newValue) =>
                setEditedDocument({ ...editedDocument, name: newValue })
              }
              disabled={saveLoading}
            />
          </div>

          <div className="flex flex-col gap-1">
            <strong className="text-gray-700">Nội dung bài viết:</strong>
            <Editor
              value={editedContent}
              onTextChange={handleContentChange}
              style={{
                height: "calc(100vh - 400px)",
                borderRadius: "0 0 8px 8px",
              }}
              readOnly={saveLoading}
            />
          </div>
        </form>
      ) : (
        <>
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: fileContent }}
          />

          {documents.name && (
            <button className="w-full">
              <a
                className="w-full flex items-center justify-center border bg-[#0457C2] border-[#0457C2] text-white hover:bg-blue-600 rounded-lg py-2 px-[10px] gap-[5px]"
                href={documents.name}
                target="_blank"
                rel="noopener noreferrer"
              >
                Link to web
              </a>
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentManagement;
